import { useEffect, useMemo, useRef, useState } from "react";
import { UserRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

/** Recipient email input with address-book suggestions.
 *  Suggestions come from saved client/entity contacts (`/contacts/emails`);
 *  typing any custom address still works — this never blocks free text. */
export const EmailCombobox = ({
  value,
  onChange,
  placeholder = "name@email.com",
  className = "",
  testId = "email-combobox",
  inputId,
  autoFocus = false,
}) => {
  const [contacts, setContacts] = useState(null); // null = not fetched yet
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef(null);

  const fetchContacts = () => {
    if (contacts !== null) return;
    setContacts([]); // mark as fetching so we only hit the API once
    api
      .get("/contacts/emails")
      .then((r) => setContacts(r.data?.contacts || []))
      .catch(() => setContacts([]));
  };

  // close when clicking outside the input + dropdown
  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const matches = useMemo(() => {
    const list = contacts || [];
    const q = String(value || "").trim().toLowerCase();
    const pool = q
      ? list.filter((c) =>
          `${c.name} ${c.email} ${c.company}`.toLowerCase().includes(q))
      : list;
    return pool.slice(0, 8);
  }, [contacts, value]);

  const pick = (c) => {
    onChange(c.email);
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (e) => {
    if (!open || matches.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => (a + 1) % matches.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => (a <= 0 ? matches.length - 1 : a - 1));
    } else if (e.key === "Enter" && active >= 0 && matches[active]) {
      e.preventDefault();
      pick(matches[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setActive(-1);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <Input
        id={inputId}
        type="email"
        autoFocus={autoFocus}
        autoComplete="off"
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => {
          fetchContacts();
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        className={className}
        data-testid={testId}
      />
      {open && matches.length > 0 && (
        <ul
          className="absolute left-0 right-0 top-full z-[250] mt-1 max-h-52 overflow-y-auto rounded-md border border-border bg-popover py-1 shadow-lg"
          data-testid={`${testId}-suggestions`}
        >
          {matches.map((c, i) => (
            <li key={c.email}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault(); // keep input focus — select before blur fires
                  pick(c);
                }}
                onMouseEnter={() => setActive(i)}
                className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-left transition-colors ${
                  i === active ? "bg-accent" : "hover:bg-accent/60"
                }`}
                data-testid={`${testId}-option-${i}`}
              >
                <UserRound className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0">
                  <span className="block truncate text-xs font-medium">
                    {c.name || c.email}
                    {c.company ? <span className="font-normal text-muted-foreground"> · {c.company}</span> : null}
                  </span>
                  <span className="block truncate text-[11px] text-muted-foreground">{c.email}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
